import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { ExclamationDiamondFill } from 'react-bootstrap-icons';
import BootstrapTable, { ColumnDescription } from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { pathnames, requestValues, requestValuesCount } from '../../api';
import { getAxiosErrorResponse } from '../../api/utils';
import AuthenticatedContext from '../../context/AuthenticatedContext';
import { isMeasurementData } from '../../models/data/MeasurementData';
import MeasurementDataAtTime from '../../models/data/MeasurementDataAtTime';
import MeasurementDeviceItem, {
  MeasurementDeviceItemArrayPropType,
} from '../../models/data/MeasurementDeviceItem';
import SearchValuesRequest from '../../models/requests/SearchValuesRequest';
import styles from './index.module.scss';

function toSizePerPageList(sizes: number[]): { text: string; value: number }[] {
  return sizes.map((value) => ({ text: `${value}件`, value }));
}

function headerFormatter(
  customerName: string,
  officeName: string,
  equipmentName: string,
  itemName?: string,
  unit?: string,
) {
  return (
    <div className="d-flex flex-column">
      <small>{customerName || '　'}</small>
      <small>{officeName || '　'}</small>
      <small>{equipmentName || '　'}</small>
      <small>{itemName || '　'}</small>
      <small>{unit || '　'}</small>
    </div>
  );
}

const TimeColumn: ColumnDescription<MeasurementDataAtTime> = {
  sort: false,
  editable: false,
  headerAlign: 'right',
  text: '',
  headerFormatter: () => {
    return headerFormatter('最終需要家', '事業所', '設備', '項目', '単位');
  },
  headerStyle: () => {
    return { whiteSpace: 'nowrap' };
  },
  dataField: 'time',
  align: 'center',
  formatter: (cell) => {
    if (typeof cell !== 'string') {
      return '';
    }
    const utcDate = parseISO(cell);
    const jstDate = utcToZonedTime(utcDate, 'Asia/Tokyo');
    const datetime = format(jstDate, 'yyyy/MM/dd HH:mm');
    return (
      <div title={cell}>
        <span>{datetime}</span>
      </div>
    );
  },
  style: () => {
    return { whiteSpace: 'nowrap' };
  },
};

function toValueColumn(
  item: MeasurementDeviceItem,
  index: number,
): ColumnDescription<MeasurementDataAtTime> {
  return {
    sort: false,
    editable: false,
    headerAlign: 'center',
    text: '',
    headerFormatter: () => {
      return headerFormatter(
        item.customerName,
        item.officeName,
        item.equipmentName,
        item.itemName,
        item.unit,
      );
    },
    headerStyle: () => {
      return { whiteSpace: 'nowrap' };
    },
    dataField: `records.${index}`,
    align: 'center',
    formatter: (cell) => {
      if (isMeasurementData(cell)) {
        const name = cell.itemName == null ? undefined : `${cell.itemName}`;
        const unit = cell.unit == null ? undefined : `(${cell.unit})`;
        return (
          <div title={[name, unit].join(' ')}>
            <span>{cell.value}</span>
          </div>
        );
      }
      return <div title="計測データなし" />;
    },
    style: () => {
      return { whiteSpace: 'nowrap' };
    },
  };
}

function toColumns(items: MeasurementDeviceItem[]): ColumnDescription[] {
  return [TimeColumn, ...items.map(toValueColumn)];
}

function toSearchValuesRequest(
  begin: Date,
  end: Date,
  items: MeasurementDeviceItem[],
): SearchValuesRequest {
  return {
    begin,
    end,
    item: items.map(({ hostName, positionNo }) => ({
      hostName,
      positionNo,
    })),
  };
}

export interface DataTableProps {
  items: MeasurementDeviceItem[];
  begin: Date;
  end: Date;
}

const DataTable: React.FunctionComponent<DataTableProps> = ({
  items,
  begin,
  end,
}) => {
  const context = useContext(AuthenticatedContext);
  // ---------------------------------------------------------------------
  // Get URL Query Parameters
  const history = useHistory();
  const url = {
    values: history.createHref({ pathname: pathnames.values }),
    valuesCount: history.createHref({ pathname: pathnames.valuesCount }),
  };
  // ---------------------------------------------------------------------
  // Status
  const [isLoadingTotalSize, setIsLoadingotalSize] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [columns, setColumns] = useState<ColumnDescription[]>([]);
  const [values, setValues] = useState<MeasurementDataAtTime[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [updateCount, setUpdateCount] = useState(0);
  // ---------------------------------------------------------------------
  // Callbacks & Functions
  // ---------------------------------------------------------------------
  // Effects
  useEffect(() => {
    let unmounted = false;
    //
    if (items.length <= 0) {
      setTotalSize(0);
      setUpdateCount(updateCount + 1); // 更新要求
    } else {
      setIsLoadingotalSize(true);
      requestValuesCount(
        url.valuesCount,
        toSearchValuesRequest(begin, end, items),
      )
        .then(({ data }) => {
          if (!unmounted) {
            setTotalSize(data);
            setUpdateCount(updateCount + 1); // 更新要求
          }
        })
        .catch((error) => {
          if (!unmounted) {
            setTotalSize(0);
            setValues([]);
          }
          const res = getAxiosErrorResponse(error);
          toast.error(
            () => {
              return (
                <>
                  <ExclamationDiamondFill />
                  <span>{` 測定データ総件数取得失敗`}</span>
                  <small>{` (${res?.status ?? '応答なし'})`}</small>
                </>
              );
            },
            {
              onClose: () => {
                if (res != null && res.status >= 400 && res.status < 500) {
                  // 認証エラーの可能性大、ログインチェック（期限切れの場合ログイン画面へ遷移する。はず）
                  context.login().catch(() => {
                    // do nothing.
                  });
                }
              },
            },
          );
        })
        .finally(() => {
          if (!unmounted) {
            setIsLoadingotalSize(false);
          }
        });
    }
    //
    return () => {
      unmounted = true;
    };
  }, [items, begin, end]);
  useEffect(() => {
    let unmounted = false;
    //
    if (items.length <= 0) {
      setColumns([]);
      setValues([]);
      setPageNo(pageNo);
      setSizePerPage(sizePerPage);
    } else {
      setIsLoadingPage(true);
      //
      const req: SearchValuesRequest = {
        ...toSearchValuesRequest(begin, end, items),
        limit: sizePerPage,
        offset: (pageNo - 1) * sizePerPage,
      };
      requestValues(url.values, req)
        .then(({ data }) => {
          if (!unmounted) {
            setColumns(toColumns(items));
            setValues(data.datas);
            setPageNo(pageNo);
            setSizePerPage(sizePerPage);
          }
        })
        .catch((error) => {
          if (!unmounted) {
            setColumns([]);
            setValues([]);
            setPageNo(pageNo);
            setSizePerPage(sizePerPage);
          }
          const res = getAxiosErrorResponse(error);
          toast.error(
            () => {
              return (
                <>
                  <ExclamationDiamondFill />
                  <span>{` 測定データ取得失敗`}</span>
                  <small>{` (${res?.status ?? '応答なし'})`}</small>
                </>
              );
            },
            {
              onClose: () => {
                if (res != null && res.status >= 400 && res.status < 500) {
                  // 認証エラーの可能性大、ログインチェック（期限切れの場合ログイン画面へ遷移する。はず）
                  context.login().catch(() => {
                    // do nothing.
                  });
                }
              },
            },
          );
        })
        .finally(() => {
          setIsLoadingPage(false);
        });
    }
    //
    return () => {
      unmounted = true;
    };
  }, [updateCount]);
  // ---------------------------------------------------------------------
  // Components
  if (isLoadingPage || isLoadingTotalSize) {
    return <Spinner animation="border" variant="primary" />;
  }
  return columns.length > 1 ? (
    <BootstrapTable
      wrapperClasses={styles.DataTableWrapper}
      classes={styles.DataTable}
      keyField="time"
      data={values}
      columns={columns}
      striped
      hover
      condensed
      remote={{
        pagination: true,
        filter: false,
        sort: false,
        cellEdit: false,
        search: false,
      }}
      bootstrap4
      pagination={paginationFactory({
        sizePerPage,
        page: pageNo,
        totalSize,
        showTotal: true,
        sizePerPageList: toSizePerPageList([10, 15, 30, 60]),
      })}
      onTableChange={(type, newState) => {
        setPageNo(newState.page);
        setSizePerPage(newState.sizePerPage);
        setUpdateCount(updateCount + 1); // 更新要求
      }}
      noDataIndication={() => <div>計測データが見つかりませんでした</div>}
    />
  ) : (
    <div />
  );
};

DataTable.propTypes = {
  items: MeasurementDeviceItemArrayPropType.isRequired,
  begin: PropTypes.instanceOf(Date).isRequired,
  end: PropTypes.instanceOf(Date).isRequired,
};

export default DataTable;
